import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import {
  applyOrganizationScopeForActor,
  orgScopedInsert,
  resolveOrganizationIdForQueries,
  stampOrganizationId,
} from "./orgQueryScope";
import {
  requireInvoiceMarkPaid,
  requireInvoiceReview,
  requireInvoiceSend,
  requireInvoiceView,
} from "./financialGuards";
import {
  assertClientOperationallyWritable,
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_CLIENT_MESSAGE,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "./operationalLifecycle";
import { isClientArchived } from "./clientVisibility";
import {
  normalizeEstimateTaxLinesForSave,
  parseEstimateTaxLineFromTree,
  taxLineToFormState,
} from "./estimateTax";
import {
  createAllocationsOnSend,
  getUnallocatedMaterialUsage,
  getUnallocatedTimeLogs,
  isDateWithinBillingPeriod,
} from "./invoiceAllocation";
import {
  buildPaymentTermsState,
  computeDueDateFromInvoiceDate,
  DEFAULT_PAYMENT_TERMS_DAYS,
  loadDefaultPaymentTermsConfig,
  loadDefaultPaymentTermsDays,
  resolvePaymentTermsPreset,
  todayIsoDate,
} from "./invoicePaymentTerms";
import {
  loadDefaultPaymentInstructions,
  serializePaymentInstructions,
} from "./invoicePaymentInstructions";
import {
  loadDefaultCustomerNotes,
  serializeCustomerNotes,
} from "./invoiceCustomerNotes";
import {
  loadDefaultInvoiceFooter,
  serializeInvoiceFooter,
} from "./invoiceFooter";
import { loadDefaultInvoiceDepositAccountId } from "./invoiceFinance";
import {
  assertAmountWithinRemaining,
  centsToDecimal,
  summarizeInvoiceSettlement,
  toCents,
} from "./invoiceMoney";
import {
  buildSeedLinesForProject,
  computeInvoiceReviewRollups,
  deleteInvoiceLineItems,
  insertInvoiceLineItem,
  isPreservedManualInvoiceLine,
  persistInvoiceReviewTotals,
  updateInvoiceLineItem,
} from "./invoiceReview";

async function scopeInvoicesQuery(query, actor) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "invoices",
  });
}

async function scopeEstimatesQuery(query, actor) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "estimates",
  });
}

async function scopeProjectsQuery(query, actor) {
  return applyOrganizationScopeForActor(query, actor, supabase, {
    tableName: "projects",
  });
}

export function generateInvoiceNumber() {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `INV-${date}-${rand}`;
}

export const INVOICE_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "archived", label: "Archived" },
];

export const INVOICE_ACTIVE_HUB_STATUSES = Object.freeze([
  "draft",
  "sent",
  "paid",
]);

export const INVOICE_STATUS_VALUES = Object.freeze(
  INVOICE_STATUS_OPTIONS.map((option) => option.value),
);

export function isValidInvoiceStatus(status) {
  return INVOICE_STATUS_VALUES.includes(status);
}

export function resolveInvoiceStatus(status) {
  return isValidInvoiceStatus(status) ? status : "draft";
}

export function formatInvoiceStatus(status) {
  const match = INVOICE_STATUS_OPTIONS.find(
    (option) => option.value === resolveInvoiceStatus(status),
  );
  return match?.label || "Draft";
}

export function formatInvoiceUpdatedAt(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function parseInvoiceTaxLine(invoice, slot) {
  if (!invoice) {
    return taxLineToFormState();
  }

  return taxLineToFormState({
    type: invoice[`tax_line_${slot}_type`],
    rate: invoice[`tax_line_${slot}_rate`],
    customName: invoice[`tax_line_${slot}_name`],
    name: invoice[`tax_line_${slot}_name`],
  });
}

export function parseInvoiceTaxLines(invoice) {
  return [1, 2].map((slot) => parseInvoiceTaxLine(invoice, slot));
}

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

function normalizeBillingDate(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 10);
}

function resolveBillingPeriodDates(options = {}) {
  const mode = options.billingPeriodMode || "entire_project";

  if (mode !== "custom_range") {
    return { startDate: null, endDate: null };
  }

  const startDate = normalizeBillingDate(options.billingPeriodStart);
  const endDate = normalizeBillingDate(options.billingPeriodEnd);

  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required for a custom billing period.");
  }

  if (startDate > endDate) {
    throw new Error("End date must be on or after the start date.");
  }

  return { startDate, endDate };
}

export function recomputeInvoiceTotals(invoice, lineItems, taxLines) {
  return computeInvoiceReviewRollups({
    lineItems,
    projectDiscountPercent: invoice?.project_discount_percent,
    goodwillAdjustment: invoice?.goodwill_adjustment,
    taxLines,
  });
}

/**
 * Org branding + joined client/project fields for invoice hubs and reports.
 * Keep in sync across list + detail loaders (no invoice-specific copies of CRM data).
 */
export const INVOICE_PAYMENTS_EMBED =
  "invoice_payments(payment_id,amount,payment_date,payment_method,payment_reference,payment_notes,recorded_by,created_at,payment_role,reversal_of_payment_id,origin)";

export const INVOICE_RELATIONS_SELECT_WITHOUT_PAYMENTS =
  "*, clients(client_name, contact_name, phone, email, address, city, province, postal_code, country), projects(project_name, project_number, site_address, client_contact_name), estimates:source_estimate_id(estimate_number), deposit_account:deposit_account_id(id, name, purpose, class, currency)";

export const INVOICE_WITH_RELATIONS_SELECT =
  `${INVOICE_RELATIONS_SELECT_WITHOUT_PAYMENTS}, ${INVOICE_PAYMENTS_EMBED}`;

/**
 * Production does not have invoice_payments until this migration is accepted.
 * A missing relationship must not take down invoice reads on that database.
 */
export function isMissingInvoicePaymentsRelation(error) {
  const text = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
  return (
    /invoice_payments/i.test(text) &&
    /relationship|schema cache|does not exist|could not find/i.test(text)
  );
}

export async function loadInvoices(actor) {
  requireInvoiceView(actor);

  const loadRows = async (select) => {
    let query = supabase
      .from("invoices")
      .select(select)
      .order("updated_at", { ascending: false });
    query = await scopeInvoicesQuery(query, actor);
    return query;
  };

  let { data, error } = await loadRows(INVOICE_WITH_RELATIONS_SELECT);
  if (isMissingInvoicePaymentsRelation(error)) {
    ({ data, error } = await loadRows(INVOICE_RELATIONS_SELECT_WITHOUT_PAYMENTS));
  }

  if (error) throw error;
  return data || [];
}

export async function loadInvoiceWithLineItems(actor, invoiceId) {
  requireInvoiceView(actor);

  const loadInvoiceRow = async (select) => {
    let invoiceQuery = supabase
      .from("invoices")
      .select(select)
      .eq("invoice_id", invoiceId)
      .maybeSingle();

    // Postgrest builders are Thenable; chain .maybeSingle() before async scope await.
    invoiceQuery = await scopeInvoicesQuery(invoiceQuery, actor);
    return invoiceQuery;
  };

  let { data: invoice, error: invoiceError } = await loadInvoiceRow(
    INVOICE_WITH_RELATIONS_SELECT,
  );
  if (isMissingInvoicePaymentsRelation(invoiceError)) {
    ({ data: invoice, error: invoiceError } = await loadInvoiceRow(
      INVOICE_RELATIONS_SELECT_WITHOUT_PAYMENTS,
    ));
  }

  if (invoiceError) throw invoiceError;
  if (!invoice) return null;

  const { data: lineItems, error: lineError } = await supabase
    .from("invoice_line_items")
    .select("*")
    .eq("invoice_id", invoiceId)
    .order("sort_order", { ascending: true });

  if (lineError) throw lineError;

  return {
    ...invoice,
    lineItems: lineItems || [],
  };
}

async function loadSourceEstimateTaxDefaults(sourceEstimateId, actor) {
  if (!sourceEstimateId) {
    return {
      taxLines: [taxLineToFormState(), taxLineToFormState()],
      sourceEstimateNumber: null,
    };
  }

  let query = supabase
    .from("estimates")
    .select(
      "estimate_number, tax_line_1_type, tax_line_1_rate, tax_line_1_name, tax_line_2_type, tax_line_2_rate, tax_line_2_name",
    )
    .eq("estimate_id", sourceEstimateId)
    .maybeSingle();

  if (actor) {
    query = await scopeEstimatesQuery(query, actor);
  }

  const { data, error } = await query;

  if (error) throw error;

  return {
    taxLines: data
      ? [1, 2].map((slot) => parseEstimateTaxLineFromTree(data, slot))
      : [taxLineToFormState(), taxLineToFormState()],
    sourceEstimateNumber: data?.estimate_number || null,
  };
}

/**
 * Seed a draft invoice from unallocated operational data (Invoice Review entry point).
 */
export async function seedInvoiceDraftFromProject(
  actor,
  projectId,
  userId,
  billingPeriodOptions = {},
) {
  requireInvoiceReview(actor);

  const { startDate, endDate } = resolveBillingPeriodDates(billingPeriodOptions);

  let projectQuery = supabase
    .from("projects")
    .select(
      "project_id, project_name, client_id, source_estimate_id, clients(client_name, archived_at, client_status)",
    )
    .eq("project_id", projectId)
    .maybeSingle();

  projectQuery = await scopeProjectsQuery(projectQuery, actor);

  const { data: project, error: projectError } = await projectQuery;

  if (projectError) throw projectError;
  if (!project) {
    throw new Error("Project not found.");
  }

  try {
    assertProjectOperationallyWritable(project, project.clients);
  } catch (lockError) {
    throw new Error(lockError?.message || OPERATIONAL_LOCK_USER_MESSAGE);
  }

  const materialRows = await getUnallocatedMaterialUsage(projectId, {
    startDate,
    endDate,
    actor,
  });
  const timeLogs = await getUnallocatedTimeLogs(projectId, {
    startDate,
    endDate,
    actor,
  });

  const seedLines = await buildSeedLinesForProject(projectId, {
    materialRows,
    timeLogs,
    actor,
  });

  const { taxLines, sourceEstimateNumber } =
    await loadSourceEstimateTaxDefaults(project.source_estimate_id, actor);

  const rollups = computeInvoiceReviewRollups({
    lineItems: seedLines,
    projectDiscountPercent: 0,
    goodwillAdjustment: 0,
    taxLines,
  });
  const taxPayload = normalizeEstimateTaxLinesForSave(taxLines);
  const seededAt = new Date().toISOString();

  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  const depositAccountId = await loadDefaultInvoiceDepositAccountId(actor);
  const paymentTermsConfig = await loadDefaultPaymentTermsConfig(actor);
  const invoiceDate = todayIsoDate();
  const paymentTermsState = buildPaymentTermsState({
    preset: paymentTermsConfig.preset,
    invoiceDate,
    dueDate: null,
    previousDays: paymentTermsConfig.days,
  });
  const paymentInstructions = serializePaymentInstructions(
    await loadDefaultPaymentInstructions(actor),
  );
  const customerNotes = serializeCustomerNotes(
    await loadDefaultCustomerNotes(actor),
  );
  const invoiceFooter = serializeInvoiceFooter(
    await loadDefaultInvoiceFooter(actor),
  );

  const invoicePayload = stampOrganizationId(
    {
      project_id: project.project_id,
      client_id: project.client_id || null,
      source_estimate_id: project.source_estimate_id || null,
      source_estimate_number: sourceEstimateNumber,
      invoice_number: generateInvoiceNumber(),
      status: "draft",
      title: project.project_name || "Untitled Project",
      billing_period_start: startDate,
      billing_period_end: endDate,
      subtotal_labour: rollups.subtotalLabour,
      subtotal_materials: rollups.subtotalMaterials,
      subtotal: rollups.subtotal,
      total_tax: rollups.totalTax,
      total_amount: rollups.totalAmount,
      project_discount_percent: 0,
      goodwill_adjustment: 0,
      deposit_account_id: depositAccountId,
      invoice_date: paymentTermsState.invoice_date,
      payment_terms_days: paymentTermsState.payment_terms_days,
      payment_terms_preset: paymentTermsState.payment_terms_preset,
      due_date: paymentTermsState.due_date,
      payment_terms_override: false,
      payment_instructions: paymentInstructions,
      payment_instructions_override: false,
      notes: customerNotes,
      customer_notes_override: false,
      invoice_footer: invoiceFooter,
      invoice_footer_override: false,
      seeded_at: seededAt,
      created_by: userId ?? null,
      updated_by: userId ?? null,
      ...taxPayload,
    },
    organizationId,
  );

  const { data: invoice, error: insertError } = await orgScopedInsert(
    supabase,
    "invoices",
    [invoicePayload],
    actor,
    { select: "invoice_id", single: true },
  );

  if (insertError) wrapSupabaseError(insertError);

  if (seedLines.length > 0) {
    const linePayload = seedLines.map((item) => ({
      ...item,
      invoice_id: invoice.invoice_id,
    }));

    const { error: lineInsertError } = await supabase
      .from("invoice_line_items")
      .insert(linePayload);

    if (lineInsertError) wrapSupabaseError(lineInsertError);
  }

  return loadInvoiceWithLineItems(actor, invoice.invoice_id);
}

export async function generateInvoiceFromProject(
  actor,
  projectId,
  userId,
  billingPeriodOptions = {},
) {
  return seedInvoiceDraftFromProject(
    actor,
    projectId,
    userId,
    billingPeriodOptions,
  );
}

/**
 * Create a standalone draft invoice (no project/job required).
 * Client is required; line items are added in the Invoice Editor.
 */
export async function createStandaloneInvoice(
  actor,
  { clientId, title, userId } = {},
) {
  requireInvoiceReview(actor);

  const trimmedClientId = String(clientId || "").trim();
  if (!trimmedClientId) {
    throw new Error("Select a client.");
  }

  const trimmedTitle = String(title || "").trim() || "Invoice";

  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) {
    throw new Error("Organization not found.");
  }

  // Postgrest builders are Thenable. Chain .maybeSingle() before awaiting the
  // async scope helper — awaiting a mid-chain builder executes it and returns
  // { data, error }, so a later .maybeSingle() throws "maybeSingle is not a function".
  let clientQuery = supabase
    .from("clients")
    .select("client_id, client_name")
    .eq("client_id", trimmedClientId)
    .is("archived_at", null)
    .maybeSingle();

  clientQuery = await applyOrganizationScopeForActor(
    clientQuery,
    actor,
    supabase,
    { tableName: "clients", organizationId },
  );

  const { data: client, error: clientError } = await clientQuery;
  if (clientError) throw clientError;
  if (!client) {
    throw new Error("Client not found.");
  }
  if (isClientArchived(client)) {
    throw new Error(OPERATIONAL_LOCK_CLIENT_MESSAGE);
  }
  try {
    assertClientOperationallyWritable(client);
  } catch (lockError) {
    throw new Error(lockError?.message || OPERATIONAL_LOCK_CLIENT_MESSAGE);
  }

  const depositAccountId = await loadDefaultInvoiceDepositAccountId(actor);
  const paymentTermsConfig = await loadDefaultPaymentTermsConfig(actor);
  const invoiceDate = todayIsoDate();
  const paymentTermsState = buildPaymentTermsState({
    preset: paymentTermsConfig.preset,
    invoiceDate,
    dueDate: null,
    previousDays: paymentTermsConfig.days,
  });
  const paymentInstructions = serializePaymentInstructions(
    await loadDefaultPaymentInstructions(actor),
  );
  const customerNotes = serializeCustomerNotes(
    await loadDefaultCustomerNotes(actor),
  );
  const invoiceFooter = serializeInvoiceFooter(
    await loadDefaultInvoiceFooter(actor),
  );
  const taxPayload = normalizeEstimateTaxLinesForSave([
    taxLineToFormState(),
    taxLineToFormState(),
  ]);
  const seededAt = new Date().toISOString();

  const invoicePayload = stampOrganizationId(
    {
      project_id: null,
      client_id: client.client_id,
      source_estimate_id: null,
      source_estimate_number: null,
      invoice_number: generateInvoiceNumber(),
      status: "draft",
      title: trimmedTitle,
      billing_period_start: null,
      billing_period_end: null,
      subtotal_labour: 0,
      subtotal_materials: 0,
      subtotal: 0,
      total_tax: 0,
      total_amount: 0,
      project_discount_percent: 0,
      goodwill_adjustment: 0,
      deposit_account_id: depositAccountId,
      invoice_date: paymentTermsState.invoice_date,
      payment_terms_days: paymentTermsState.payment_terms_days,
      payment_terms_preset: paymentTermsState.payment_terms_preset,
      due_date: paymentTermsState.due_date,
      payment_terms_override: false,
      payment_instructions: paymentInstructions,
      payment_instructions_override: false,
      notes: customerNotes,
      customer_notes_override: false,
      invoice_footer: invoiceFooter,
      invoice_footer_override: false,
      seeded_at: seededAt,
      created_by: userId ?? null,
      updated_by: userId ?? null,
      ...taxPayload,
    },
    organizationId,
  );

  const { data: invoice, error: insertError } = await orgScopedInsert(
    supabase,
    "invoices",
    [invoicePayload],
    actor,
    { select: "invoice_id", single: true },
  );

  if (insertError) wrapSupabaseError(insertError);

  return loadInvoiceWithLineItems(actor, invoice.invoice_id);
}

/**
 * Persist draft billing-period dates without reseeding line items.
 * Preview / report options may update the window label; only
 * `applyInvoiceBillingPeriod` (Refresh from Project) re-imports labour/materials.
 */
export async function updateDraftInvoiceBillingPeriod(
  actor,
  invoiceId,
  billingPeriodOptions = {},
  userId = null,
) {
  requireInvoiceReview(actor);

  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }

  if (resolveInvoiceStatus(invoice.status) !== "draft") {
    return invoice;
  }

  const { startDate, endDate } = resolveBillingPeriodDates(billingPeriodOptions);
  const currentStart = normalizeBillingDate(invoice.billing_period_start);
  const currentEnd = normalizeBillingDate(invoice.billing_period_end);

  if (currentStart === startDate && currentEnd === endDate) {
    return invoice;
  }

  const { error } = await supabase
    .from("invoices")
    .update({
      billing_period_start: startDate,
      billing_period_end: endDate,
      updated_by: userId ?? null,
    })
    .eq("invoice_id", invoiceId)
    .eq("status", "draft");

  if (error) wrapSupabaseError(error);

  return loadInvoiceWithLineItems(actor, invoiceId);
}

export async function applyInvoiceBillingPeriod(
  actor,
  invoiceId,
  userId,
  billingPeriodOptions = {},
) {
  requireInvoiceReview(actor);

  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }

  if (resolveInvoiceStatus(invoice.status) !== "draft") {
    return invoice;
  }

  if (!invoice.project_id) {
    throw new Error(
      "This invoice is not linked to a project. Add labour and materials manually.",
    );
  }

  const { startDate, endDate } = resolveBillingPeriodDates(billingPeriodOptions);

  const materialRows = await getUnallocatedMaterialUsage(invoice.project_id, {
    startDate,
    endDate,
    actor,
  });
  const timeLogs = await getUnallocatedTimeLogs(invoice.project_id, {
    startDate,
    endDate,
    actor,
  });

  const seedLines = await buildSeedLinesForProject(invoice.project_id, {
    materialRows,
    timeLogs,
    actor,
  });

  const taxLines = parseInvoiceTaxLines(invoice);
  const preservedLines = (invoice.lineItems || []).filter(
    isPreservedManualInvoiceLine,
  );
  const preservedIds = new Set(
    preservedLines.map((line) => line.line_id).filter(Boolean),
  );
  const removableIds = (invoice.lineItems || [])
    .map((line) => line.line_id)
    .filter((lineId) => lineId && !preservedIds.has(lineId));

  if (removableIds.length > 0) {
    await deleteInvoiceLineItems(actor, removableIds);
  }

  const sortOffset = preservedLines.length;
  const seedPayload = seedLines.map((item, index) => ({
    ...item,
    sort_order: sortOffset + index,
    invoice_id: invoiceId,
  }));

  if (seedPayload.length > 0) {
    const { error: lineInsertError } = await supabase
      .from("invoice_line_items")
      .insert(seedPayload);

    if (lineInsertError) wrapSupabaseError(lineInsertError);
  }

  const mergedLines = [
    ...preservedLines.map((line, index) => ({
      ...line,
      sort_order: index,
    })),
    ...seedPayload,
  ];

  await persistInvoiceReviewTotals(actor, invoiceId, {
    lineItems: mergedLines,
    projectDiscountPercent: invoice.project_discount_percent,
    goodwillAdjustment: invoice.goodwill_adjustment,
    reviewNotes: invoice.review_notes,
    taxLines,
    userId,
    billingPeriodStart: startDate,
    billingPeriodEnd: endDate,
    seededAt: new Date().toISOString(),
  });

  return loadInvoiceWithLineItems(actor, invoiceId);
}

const REVIEW_LINE_PERSIST_FIELDS = [
  "quantity",
  "unit_amount",
  "discount_percent",
  "line_status",
  "pricing_source",
  "line_total",
  "description",
  "detail",
  "line_type",
  "unit_label",
  "metadata",
  "sort_order",
];

async function persistInvoiceReviewLineChanges(
  actor,
  invoiceId,
  previousLines = [],
  nextLines = [],
  userId,
) {
  const previousById = new Map(
    (previousLines || []).map((line) => [line.line_id, line]),
  );
  const nextById = new Map(
    (nextLines || [])
      .filter((line) => line?.line_id)
      .map((line) => [line.line_id, line]),
  );

  const removedIds = [...previousById.keys()].filter(
    (lineId) => !nextById.has(lineId),
  );
  if (removedIds.length > 0) {
    await deleteInvoiceLineItems(actor, removedIds);
  }

  for (const line of nextLines || []) {
    if (!line?.line_id) continue;

    const previous = previousById.get(line.line_id);
    if (!previous) {
      await insertInvoiceLineItem(actor, invoiceId, line, userId);
      continue;
    }

    const changes = {};
    for (const field of REVIEW_LINE_PERSIST_FIELDS) {
      const nextValue = line[field];
      const previousValue = previous[field];
      const changed =
        field === "metadata"
          ? JSON.stringify(nextValue ?? null) !==
            JSON.stringify(previousValue ?? null)
          : nextValue !== previousValue;
      if (changed) {
        changes[field] = nextValue;
      }
    }

    if (Object.keys(changes).length > 0) {
      await updateInvoiceLineItem(actor, line.line_id, changes, userId);
    }
  }
}

export async function updateInvoiceDraft(
  actor,
  invoiceId,
  {
    invoiceNumber,
    notes,
    clientReference,
    taxLines,
    projectDiscountPercent,
    goodwillAdjustment,
    reviewNotes,
    lineItems,
    depositAccountId,
    invoiceDate,
    paymentTermsPreset,
    paymentTermsDays,
    dueDate,
    paymentInstructions,
    paymentInstructionsOverride,
    paymentTermsOverride,
    customerNotesOverride,
    invoiceFooter,
    invoiceFooterOverride,
    userId,
  },
  invoiceRecord,
) {
  requireInvoiceReview(actor);

  if (resolveInvoiceStatus(invoiceRecord?.status) !== "draft") {
    throw new Error("Only draft invoices can be edited.");
  }

  if (lineItems) {
    await persistInvoiceReviewLineChanges(
      actor,
      invoiceId,
      invoiceRecord.lineItems || [],
      lineItems,
      userId,
    );
  }

  const effectiveLineItems = lineItems ?? invoiceRecord.lineItems ?? [];
  const taxPayload = normalizeEstimateTaxLinesForSave(taxLines);
  const rollups = recomputeInvoiceTotals(
    {
      ...invoiceRecord,
      project_discount_percent:
        projectDiscountPercent ?? invoiceRecord.project_discount_percent,
      goodwill_adjustment:
        goodwillAdjustment ?? invoiceRecord.goodwill_adjustment,
    },
    effectiveLineItems,
    taxLines,
  );

  const payload = {
    invoice_number: invoiceNumber?.trim() || invoiceRecord.invoice_number,
    notes:
      notes !== undefined
        ? serializeCustomerNotes(notes)
        : invoiceRecord.notes ?? null,
    client_reference: clientReference ?? invoiceRecord.client_reference ?? null,
    project_discount_percent:
      projectDiscountPercent ?? invoiceRecord.project_discount_percent ?? 0,
    goodwill_adjustment:
      goodwillAdjustment ?? invoiceRecord.goodwill_adjustment ?? 0,
    review_notes: reviewNotes ?? invoiceRecord.review_notes ?? null,
    subtotal_labour: rollups.subtotalLabour,
    subtotal_materials: rollups.subtotalMaterials,
    subtotal: rollups.subtotal,
    total_tax: rollups.totalTax,
    total_amount: rollups.totalAmount,
    updated_by: userId ?? null,
    ...taxPayload,
  };

  if (depositAccountId !== undefined) {
    payload.deposit_account_id = depositAccountId || null;
  }
  if (invoiceDate !== undefined) {
    payload.invoice_date = invoiceDate || null;
  }
  if (paymentTermsPreset !== undefined) {
    payload.payment_terms_preset = paymentTermsPreset || null;
  }
  if (paymentTermsDays !== undefined) {
    payload.payment_terms_days =
      paymentTermsDays === "" || paymentTermsDays == null
        ? null
        : Number(paymentTermsDays);
  }
  if (dueDate !== undefined) {
    payload.due_date = dueDate || null;
  }
  if (paymentInstructions !== undefined) {
    payload.payment_instructions =
      serializePaymentInstructions(paymentInstructions);
  }
  if (paymentInstructionsOverride !== undefined) {
    payload.payment_instructions_override =
      paymentInstructionsOverride === true;
  }
  if (paymentTermsOverride !== undefined) {
    payload.payment_terms_override = paymentTermsOverride === true;
  }
  if (customerNotesOverride !== undefined) {
    payload.customer_notes_override = customerNotesOverride === true;
  }
  if (invoiceFooter !== undefined) {
    payload.invoice_footer = serializeInvoiceFooter(invoiceFooter);
  }
  if (invoiceFooterOverride !== undefined) {
    payload.invoice_footer_override = invoiceFooterOverride === true;
  }

  let updateQuery = supabase
    .from("invoices")
    .update(payload)
    .eq("invoice_id", invoiceId)
    .eq("status", "draft");

  updateQuery = await scopeInvoicesQuery(updateQuery, actor);

  const { error } = await updateQuery;

  if (error) wrapSupabaseError(error);
}

export async function markInvoiceSent(actor, invoiceId, userId) {
  requireInvoiceSend(actor);

  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }

  if (resolveInvoiceStatus(invoice.status) !== "draft") {
    throw new Error("Only draft invoices can be marked sent.");
  }

  await createAllocationsOnSend(invoiceId, invoice.lineItems || []);

  const sentDate = new Date();
  const sentAt = sentDate.toISOString();
  const orgTerms = await loadDefaultPaymentTermsConfig(actor);
  let invoiceDate =
    (invoice.invoice_date && String(invoice.invoice_date).slice(0, 10)) ||
    todayIsoDate(sentDate);
  let paymentTermsDays =
    invoice.payment_terms_days != null &&
    Number.isFinite(Number(invoice.payment_terms_days)) &&
    Number(invoice.payment_terms_days) >= 0
      ? Number(invoice.payment_terms_days)
      : orgTerms.days ?? DEFAULT_PAYMENT_TERMS_DAYS;
  let paymentTermsPreset =
    invoice.payment_terms_preset ||
    resolvePaymentTermsPreset(null, paymentTermsDays);
  let dueDate =
    (invoice.due_date && String(invoice.due_date).slice(0, 10)) ||
    computeDueDateFromInvoiceDate(invoiceDate, paymentTermsDays);

  if (invoice.payment_terms_override !== true) {
    const frozenTerms = buildPaymentTermsState({
      preset: orgTerms.preset,
      invoiceDate,
      dueDate: null,
      previousDays: orgTerms.days,
    });
    invoiceDate = frozenTerms.invoice_date;
    paymentTermsDays = frozenTerms.payment_terms_days;
    paymentTermsPreset = frozenTerms.payment_terms_preset;
    dueDate = frozenTerms.due_date;
  }

  // Freeze effective payment instructions on send so PDFs stay stable if org settings change.
  let paymentInstructions = serializePaymentInstructions(
    invoice.payment_instructions,
  );
  if (invoice.payment_instructions_override !== true) {
    paymentInstructions = serializePaymentInstructions(
      await loadDefaultPaymentInstructions(actor),
    );
  }

  let notes = serializeCustomerNotes(invoice.notes);
  if (invoice.customer_notes_override !== true) {
    notes = serializeCustomerNotes(await loadDefaultCustomerNotes(actor));
  }

  let invoiceFooter = serializeInvoiceFooter(invoice.invoice_footer);
  if (invoice.invoice_footer_override !== true) {
    invoiceFooter = serializeInvoiceFooter(
      await loadDefaultInvoiceFooter(actor),
    );
  }

  let sentQuery = supabase
    .from("invoices")
    .update({
      status: "sent",
      sent_at: sentAt,
      invoice_date: invoiceDate,
      payment_terms_days: paymentTermsDays,
      payment_terms_preset: paymentTermsPreset,
      due_date: dueDate,
      payment_instructions: paymentInstructions,
      notes,
      invoice_footer: invoiceFooter,
      updated_by: userId ?? null,
    })
    .eq("invoice_id", invoiceId)
    .eq("status", "draft");

  sentQuery = await scopeInvoicesQuery(sentQuery, actor);

  const { error } = await sentQuery;

  if (error) wrapSupabaseError(error);
}

function resolveDepositAccountId(invoice, orgDefaultAccountId) {
  return invoice?.deposit_account_id || orgDefaultAccountId || null;
}

/**
 * Record one receipt against an issued invoice.
 * The database locks the invoice, rejects amounts above the remaining balance,
 * and posts Finance for this receipt only.
 */
export async function recordInvoicePayment(
  actor,
  invoiceId,
  userId,
  paymentDetails = {},
) {
  void userId;
  requireInvoiceMarkPaid(actor);

  if (!invoiceId) {
    throw new Error("Invoice id is required to record a payment.");
  }

  const amountCents = toCents(paymentDetails.amount);
  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }
  if (resolveInvoiceStatus(invoice.status) !== "sent") {
    throw new Error("Only issued invoices can receive a payment.");
  }

  const settlement = summarizeInvoiceSettlement(invoice);
  assertAmountWithinRemaining(
    settlement.totalCents,
    settlement.paidCents,
    amountCents,
  );

  const idempotencyKey = String(paymentDetails.idempotencyKey || "").trim();
  if (!idempotencyKey) {
    throw new Error("Payment could not be recorded. Try again.");
  }

  const paymentDate =
    normalizeBillingDate(paymentDetails.paymentDate) ||
    new Date().toISOString().slice(0, 10);
  const orgDefaultAccountId = await loadDefaultInvoiceDepositAccountId(actor);

  const { data, error } = await supabase.rpc("cp_record_invoice_payment", {
    p_invoice_id: invoiceId,
    p_amount: centsToDecimal(amountCents),
    p_payment_date: paymentDate,
    p_payment_method: paymentDetails.paymentMethod || null,
    p_payment_reference: paymentDetails.paymentReference || null,
    p_payment_notes: paymentDetails.paymentNotes || null,
    p_idempotency_key: idempotencyKey,
    p_deposit_account_id: resolveDepositAccountId(invoice, orgDefaultAccountId),
  });

  if (error) wrapSupabaseError(error);
  return data;
}

/**
 * Pay the remaining balance in one receipt.
 * Callers that collect an amount use recordInvoicePayment instead.
 */
export async function markInvoicePaid(
  actor,
  invoiceId,
  userId,
  paymentDetails = {},
) {
  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }
  if (resolveInvoiceStatus(invoice.status) !== "sent") {
    throw new Error("Only issued invoices can receive a payment.");
  }

  const settlement = summarizeInvoiceSettlement(invoice);
  if (settlement.outstandingCents <= 0) {
    throw new Error("This invoice has no remaining balance.");
  }

  const amount =
    paymentDetails.amount != null && paymentDetails.amount !== ""
      ? paymentDetails.amount
      : centsToDecimal(settlement.outstandingCents);

  return recordInvoicePayment(actor, invoiceId, userId, {
    ...paymentDetails,
    amount,
    idempotencyKey:
      paymentDetails.idempotencyKey ||
      `pay-remaining:${invoiceId}:${centsToDecimal(settlement.outstandingCents)}`,
  });
}

export async function reverseInvoicePayment(
  actor,
  invoiceId,
  userId,
  paymentId,
) {
  void userId;
  requireInvoiceMarkPaid(actor);

  if (!paymentId) {
    throw new Error("Choose the payment to reverse.");
  }

  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }

  const status = resolveInvoiceStatus(invoice.status);
  if (status !== "sent" && status !== "paid") {
    throw new Error("This invoice cannot reverse a payment.");
  }

  const orgDefaultAccountId = await loadDefaultInvoiceDepositAccountId(actor);
  const reversalDate = new Date().toISOString().slice(0, 10);

  const { data, error } = await supabase.rpc("cp_reverse_invoice_payment", {
    p_payment_id: paymentId,
    p_idempotency_key: `reverse:${paymentId}`,
    p_reversal_date: reversalDate,
    p_deposit_account_id: resolveDepositAccountId(invoice, orgDefaultAccountId),
  });

  if (error) wrapSupabaseError(error);
  return data;
}

export async function reopenInvoiceToDraft(actor, invoiceId) {
  requireInvoiceSend(actor);

  if (!invoiceId) {
    throw new Error("Invoice id is required to reopen to draft.");
  }

  const invoice = await loadInvoiceWithLineItems(actor, invoiceId);
  if (!invoice) {
    throw new Error("Invoice not found.");
  }

  if (resolveInvoiceStatus(invoice.status) !== "sent") {
    throw new Error("Only unpaid sent invoices can be reopened to draft.");
  }

  if (invoice.paid_at) {
    throw new Error("Paid invoices cannot be reopened to draft.");
  }

  if (summarizeInvoiceSettlement(invoice).hasUnreversedPayment) {
    throw new Error("Invoices with recorded payments cannot be reopened to draft.");
  }

  const { data, error } = await supabase.rpc("reopen_invoice_to_draft", {
    p_invoice_id: invoiceId,
  });

  if (error) wrapSupabaseError(error);

  const raw = data && typeof data === "object" ? data : {};
  return {
    invoiceId: raw.invoice_id || invoiceId,
    status: raw.status || "draft",
    usageAllocationsDeleted: Number(raw.usage_allocations_deleted || 0),
    labourAllocationsDeleted: Number(raw.labour_allocations_deleted || 0),
    raw,
  };
}

export async function archiveInvoice(actor, invoiceId, userId) {
  requireInvoiceReview(actor);

  let archiveQuery = supabase
    .from("invoices")
    .update({
      status: "archived",
      updated_by: userId ?? null,
    })
    .eq("invoice_id", invoiceId)
    .in("status", ["sent", "paid"]);

  archiveQuery = await scopeInvoicesQuery(archiveQuery, actor);

  const { error } = await archiveQuery;

  if (error) wrapSupabaseError(error);
}

export async function deleteDraftInvoice(actor, invoiceId) {
  requireInvoiceReview(actor);

  let deleteQuery = supabase
    .from("invoices")
    .delete()
    .eq("invoice_id", invoiceId)
    .eq("status", "draft");

  deleteQuery = await scopeInvoicesQuery(deleteQuery, actor);

  const { error } = await deleteQuery;

  if (error) wrapSupabaseError(error);
}

export { isDateWithinBillingPeriod };
